import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }

import { onMounted, watch, ref, computed } from 'vue';

import { onUnmounted } from 'vue';

export default /*@__PURE__*/_defineComponent({
  __name: 'GoogleAds',
  props: {
  conversionId: {
    type: String,
    required: true
  },
  conversionLabel: {
    type: String,
    default: ''
  }
},
  setup(__props, { expose: __expose }) {

const props = __props;

const isGoogleAdsInitialized = ref(false);
const consentGiven = ref(false); // Track consent state

// Check consent from URL parameters or internal state
const hasConsent = computed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const trackingOptIn = urlParams.get('trackingOptIn');
  
  // If we're in WebView context (trackingOptIn parameter exists)
  if (trackingOptIn !== null) {
    return trackingOptIn === 'true';
  }
  
  // If we're on standalone webpage, use internal consent state
  return consentGiven.value;
});

// Initialize Google Ads tracking
const initializeGoogleAds = (id: string, label: string) => {
  if (!id || !hasConsent.value || isGoogleAdsInitialized.value) return;
  
  // Remove any existing Google Ads scripts
  const existingScript = document.getElementById('google-ads-script');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create and add the Google Ads script
  const script = document.createElement('script');
  script.id = 'google-ads-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
  
  // Initialize Google's gtag
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  gtag('js', new Date());
  gtag('config', id);
  
  // Store references for later use
  (window as any).gtag = gtag;
  
  // If a conversion label is provided, you can track specific conversions
  if (label) {
    gtag('event', 'conversion', {
      'send_to': `${id}/${label}`,
      'page_location': window.location.href
    });
  }
  
  isGoogleAdsInitialized.value = true;
};

// Revoke Google Ads consent and clear
const revokeGoogleAdsConsent = () => {
  if ((window as any).gtag && isGoogleAdsInitialized.value) {
    // Remove the script
    const existingScript = document.getElementById('google-ads-script');
    if (existingScript) {
      existingScript.remove();
    }
    // Clear gtag
    delete (window as any).gtag;
    isGoogleAdsInitialized.value = false;
  }
};

// Handle consent changes
const handleConsentChange = () => {
  if (hasConsent.value && props.conversionId) {
    initializeGoogleAds(props.conversionId, props.conversionLabel);
  } else {
    revokeGoogleAdsConsent();
  }
};

// Initialize when component mounts
onMounted(() => {
  window.addEventListener('message', handleConsentMessage);
  handleConsentChange();
  if(!window.ReactNativeWebView) {
    // If not in WebView, check local storage
    consentGiven.value = localStorage.getItem('trackingOptIn') === 'true';
  }
});

// Watch for conversion ID/label changes
watch([() => props.conversionId, () => props.conversionLabel], ([newId, newLabel]) => {
  if (newId && hasConsent.value) {
    // Reset initialization flag when ID changes
    isGoogleAdsInitialized.value = false;
    initializeGoogleAds(newId, newLabel);
  }
});

// Watch for consent changes
watch(hasConsent, () => {
  handleConsentChange();
});

// Listen for consent updates (from banner or React Native)
const handleConsentMessage = (event: MessageEvent) => {
  if (event.data && typeof event.data === 'string') {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'CONSENT_UPDATED') {
        // Update internal consent state for standalone webpage
        consentGiven.value = data.payload.action === 'accept';
      }
    } catch (e) {
      // Ignore non-JSON messages
    }
  }
};

// Method to track specific conversions (can be exposed for parent components)
const trackConversion = (value = 0, currency = 'USD', transactionId = '') => {
  if (!props.conversionId || !props.conversionLabel || !(window as any).gtag || !hasConsent.value) return;
  
  const conversionData: any = {
    'send_to': `${props.conversionId}/${props.conversionLabel}`,
    'value': value,
    'currency': currency
  };
  
  if (transactionId) {
    conversionData.transaction_id = transactionId;
  }
  
  (window as any).gtag('event', 'conversion', conversionData);
};

// Expose methods for parent components
__expose({ trackConversion });

// Cleanup
onUnmounted(() => {
  window.removeEventListener('message', handleConsentMessage);
});

return (_ctx: any,_cache: any) => {
  return (__props.conversionId && hasConsent.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1))
    : _createCommentVNode("", true)
}
}

})